import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Avatar, Button } from '@mela/design-system';
import { BrandMark } from './BrandMark';
import { useSession } from '../lib/useSession';
import { useMelaIdentity } from '../lib/useMelaIdentity';

const links = [
  { to: '/discover', label: 'Discover events' },
  { to: '/#exhibitors', label: 'For exhibitors' },
  { to: '/#organizers', label: 'For organizers' },
  { to: '/pricing', label: 'Pricing' },
];

export function Nav() {
  const { session, loading, signOut } = useSession();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-md backdrop-saturate-150 border-b border-ink-200/60">
      <div className="container-mela py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 font-display font-extrabold text-[22px] tracking-tight text-ink-900"
        >
          <BrandMark />
          <span>mela</span>
        </Link>

        <div className="hidden md:flex gap-8 items-center">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className="text-ink-700 hover:text-ink-900 text-sm font-medium transition-colors duration-fast"
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="flex gap-2.5 items-center">
          {loading ? (
            <div className="w-[110px] h-[38px] rounded-md bg-ink-100 animate-shimmer bg-gradient-to-r from-ink-100 via-ink-200 to-ink-100 bg-[length:200%_100%]" />
          ) : session ? (
            <UserMenu onSignOut={() => signOut().then(() => navigate('/'))} />
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex">
                <Button variant="ghost" size="md">
                  Sign in
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="primary" size="md">
                  Get a Mela ID
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function UserMenu({ onSignOut }: { onSignOut: () => void }) {
  const { identity } = useMelaIdentity();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initials =
    (identity?.display_name ?? '')
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || 'M';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full p-1 hover:bg-ink-100 transition-colors duration-fast"
        aria-label="Account menu"
        aria-expanded={open}
      >
        <Avatar size="sm" initials={initials} src={identity?.avatar_url ?? undefined} />
        <span className="hidden sm:inline text-[13px] font-medium text-ink-700 pr-1">
          @{identity?.username ?? '…'}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 bg-white border border-ink-200 rounded-xl shadow-floating overflow-hidden animate-fade-up"
        >
          <div className="px-4 py-3 border-b border-ink-200">
            <div className="text-[13px] font-bold text-ink-900 truncate">
              {identity?.display_name ?? 'Loading…'}
            </div>
            {identity?.username && (
              <div className="text-[11px] font-mono text-ink-500 truncate">
                @{identity.username}
              </div>
            )}
          </div>
          <MenuItem to="/me" onClick={() => setOpen(false)}>
            Edit profile
          </MenuItem>
          {identity?.username && (
            <MenuItem to={`/in/${identity.username}`} onClick={() => setOpen(false)}>
              View public profile
            </MenuItem>
          )}
          <div className="border-t border-ink-200" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="w-full text-left px-4 py-2.5 text-[13px] text-ink-700 hover:bg-ink-100 transition-colors duration-fast"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-4 py-2.5 text-[13px] text-ink-700 hover:bg-ink-100 transition-colors duration-fast"
      role="menuitem"
    >
      {children}
    </Link>
  );
}
